import React from 'react';
import {
  View,
  Modal,
  StyleSheet,
  TouchableOpacity,
  Pressable,
  Platform,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useTheme } from '../../theme/ThemeContext';
import ThemedText from '../common/ThemedText';
import { VIDEO_MAX_DURATION, VIDEO_MIN_DURATION } from '../../config/env';

export type VideoSource = 'library' | 'camera';

interface VideoSourcePickerProps {
  visible: boolean;
  onSelect: (source: VideoSource) => void;
  onCancel: () => void;
  maxDuration?: number;
}

const OPTIONS: Array<{
  source: VideoSource;
  icon: keyof typeof Ionicons.glyphMap;
  title: string;
  description: string;
}> = [
  {
    source: 'library',
    icon: 'images-outline',
    title: 'Upload from Gallery',
    description: 'Choose an existing video from your device',
  },
  {
    source: 'camera',
    icon: 'videocam-outline',
    title: 'Record Video',
    description: 'Capture a new video using your camera',
  },
];

const VideoSourcePicker: React.FC<VideoSourcePickerProps> = ({
  visible,
  onSelect,
  onCancel,
  maxDuration,
}) => {
  const { theme, isDark } = useTheme();
  const insets = useSafeAreaInsets();
  const isWeb = Platform.OS === 'web';
  const effectiveMaxDuration = maxDuration ?? VIDEO_MAX_DURATION;

  return (
    <Modal
      visible={visible}
      transparent
      animationType={isWeb ? 'fade' : 'slide'}
      onRequestClose={onCancel}
    >
      <View style={[styles.overlay, isWeb && styles.overlayCentered]}>
        <Pressable style={styles.backdrop} onPress={onCancel} />

        <View
          style={[
            styles.sheet,
            {
              backgroundColor: theme.card,
              borderColor: isDark ? 'rgba(255,255,255,0.08)' : theme.border,
              paddingBottom: isWeb ? 20 : Math.max(insets.bottom, 16) + 8,
            },
            isWeb && styles.sheetCentered,
          ]}
        >
          {!isWeb && (
            <View style={styles.handleWrap}>
              <View style={[styles.handle, { backgroundColor: theme.borderDark }]} />
            </View>
          )}

          <View style={styles.header}>
            <View style={[styles.heroIcon, { backgroundColor: theme.accent + '22' }]}>
              <Ionicons name="videocam" size={28} color={theme.accent} />
            </View>
            <ThemedText style={[styles.title, { color: theme.text }]}>
              Add a Video
            </ThemedText>
            <ThemedText style={[styles.subtitle, { color: theme.textSecondary }]}>
              Share a short video with the community
            </ThemedText>
            <View
              style={[
                styles.durationChip,
                {
                  backgroundColor: theme.accent + '18',
                  borderColor: theme.accent + '55',
                },
              ]}
            >
              <Ionicons name="time-outline" size={15} color={theme.accent} />
              <ThemedText style={[styles.durationText, { color: theme.accent }]}>
                {VIDEO_MIN_DURATION}–{effectiveMaxDuration} seconds
              </ThemedText>
            </View>
          </View>

          <View style={styles.options}>
            {OPTIONS.map((option) => (
              <Pressable
                key={option.source}
                style={({ pressed }) => [
                  styles.optionCard,
                  {
                    backgroundColor: isDark ? theme.backgroundTertiary : theme.backgroundSecondary,
                    borderColor: isDark ? 'rgba(255,255,255,0.08)' : theme.border,
                    opacity: pressed ? 0.85 : 1,
                    transform: [{ scale: pressed ? 0.985 : 1 }],
                  },
                ]}
                onPress={() => onSelect(option.source)}
                android_ripple={{ color: theme.accent + '33' }}
              >
                <View style={[styles.optionIcon, { backgroundColor: theme.accent + '22' }]}>
                  <Ionicons name={option.icon} size={22} color={theme.accent} />
                </View>
                <View style={styles.optionCopy}>
                  <ThemedText style={[styles.optionTitle, { color: theme.text }]}>
                    {option.title}
                  </ThemedText>
                  <ThemedText style={[styles.optionDescription, { color: theme.textSecondary }]}>
                    {option.description}
                  </ThemedText>
                </View>
                <Ionicons name="chevron-forward" size={20} color={theme.textTertiary} />
              </Pressable>
            ))}
          </View>

          <TouchableOpacity style={styles.cancelButton} onPress={onCancel} activeOpacity={0.7}>
            <ThemedText style={[styles.cancelText, { color: theme.textSecondary }]}>
              Cancel
            </ThemedText>
          </TouchableOpacity>
        </View>
      </View>
    </Modal>
  );
};

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    justifyContent: 'flex-end',
  },
  overlayCentered: {
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  backdrop: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: 'rgba(0, 0, 0, 0.62)',
  },
  sheet: {
    borderTopLeftRadius: 28,
    borderTopRightRadius: 28,
    borderWidth: 1,
    paddingHorizontal: 20,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: -8 },
    shadowOpacity: 0.28,
    shadowRadius: 20,
    elevation: 18,
  },
  sheetCentered: {
    width: '100%',
    maxWidth: 420,
    borderRadius: 28,
    borderTopLeftRadius: 28,
    borderTopRightRadius: 28,
  },
  handleWrap: {
    alignItems: 'center',
    paddingTop: 10,
  },
  handle: {
    width: 40,
    height: 4,
    borderRadius: 2,
  },
  header: {
    alignItems: 'center',
    paddingTop: 12,
    paddingBottom: 8,
  },
  heroIcon: {
    width: 64,
    height: 64,
    borderRadius: 20,
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 14,
  },
  title: {
    fontSize: 22,
    fontWeight: '700',
    marginBottom: 6,
  },
  subtitle: {
    fontSize: 14,
    textAlign: 'center',
    lineHeight: 20,
    marginBottom: 14,
  },
  durationChip: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 999,
    borderWidth: 1,
  },
  durationText: {
    fontSize: 13,
    fontWeight: '600',
  },
  options: {
    gap: 12,
    paddingVertical: 16,
  },
  optionCard: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 16,
    borderRadius: 16,
    borderWidth: 1,
    overflow: 'hidden',
  },
  optionIcon: {
    width: 48,
    height: 48,
    borderRadius: 14,
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 12,
  },
  optionCopy: {
    flex: 1,
    flexShrink: 1,
  },
  optionTitle: {
    fontSize: 16,
    fontWeight: '600',
    marginBottom: 2,
  },
  optionDescription: {
    fontSize: 13,
    lineHeight: 18,
  },
  cancelButton: {
    alignItems: 'center',
    paddingVertical: 12,
  },
  cancelText: {
    fontSize: 15,
    fontWeight: '600',
  },
});

export default VideoSourcePicker;
